import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import ArticleLayout, { H2, H3, P, Li, Ul, Blockquote, DataBox } from './ArticleLayout';
import { ARTICLES } from './RecursosPage';

const meta = ARTICLES[1]; // deepseek-vs-gpt-vs-claude

const ModelRow = ({ model, task, verdict, color }) => (
  <div className={`rounded-xl border p-4 mb-3 ${color}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <p className="text-white text-sm font-black mb-1">{model}</p>
        <p className="text-slate-300 text-xs leading-relaxed">{task}</p>
      </div>
      <span className="text-[10px] font-bold text-slate-400 flex-shrink-0 pt-0.5">{verdict}</span>
    </div>
  </div>
);

export default function ArticuloDeepSeek() {
  return (
    <>
      <SEO
        title="DeepSeek vs GPT vs Claude en automatización empresarial | Neuralia 369"
        description="Comparativa honesta sobre cuándo DeepSeek reduce costes reales, cuándo GPT ofrece mejor ecosistema y cuándo Claude es la opción correcta para razonamiento profundo."
        path="/recursos/deepseek-vs-gpt-vs-claude"
      />
      <ArticleLayout
        meta={meta}
        cta={{
          title: 'Solicitar activación con el modelo que elijas',
          desc: 'Aynara enruta cada tarea al modelo más eficiente según tu tipo de operación. Tú eliges, nosotros medimos.',
          href: '/activar',
          label: 'Solicitar activación',
          secondary: { href: '/integraciones/deepseek', label: 'Ver calculadora de consumo' },
        }}
      >
        {/* ── INTRO ── */}
        <P>
          No existe el modelo de lenguaje perfecto para todo. Existen modelos optimizados para distintos
          tipos de tarea, con distintos perfiles de coste, latencia y capacidad de razonamiento.
          Este artículo no tiene patrocinador ni acuerdo comercial con ninguno de los tres proveedores.
          El objetivo es dar criterios prácticos para elegir bien según el tipo de automatización que necesitas.
        </P>
        <P>
          Hablaremos de cuatro modelos disponibles en Aynara: DeepSeek V3/R1, GPT-4o/GPT-4o-mini,
          Claude Haiku/Sonnet y Kimi k1.5. Todos tienen casos de uso legítimos. Ninguno es universalmente
          superior.
        </P>

        {/* ── SECCIÓN 1 ── */}
        <H2>El error más común al elegir un modelo</H2>
        <P>
          La mayoría de las empresas eligen el modelo más conocido o el más reciente y lo aplican a todas
          las tareas. GPT-4o para clasificar leads. GPT-4o para redactar correos. GPT-4o para analizar contratos.
          GPT-4o para responder soporte. El resultado es un coste de inferencia más alto de lo necesario
          y, en algunos casos, peor rendimiento en tareas donde un modelo especializado habría funcionado mejor.
        </P>
        <P>
          La pregunta correcta no es "¿cuál es el mejor modelo?" sino "¿cuál es el modelo más eficiente
          para esta tarea específica, a este volumen y con estos requisitos de precisión?"
        </P>

        <Blockquote source="Principio de enrutamiento — Aynara">
          Enrutamos cada tarea al modelo más eficiente para esa operación. Si una clasificación cuesta
          7 veces menos con DeepSeek y la precisión es equivalente, usar GPT-4o no es una elección
          de calidad, es un coste evitable.
        </Blockquote>

        {/* ── SECCIÓN 2 ── */}
        <H2>DeepSeek V3 / R1: cuándo es la elección correcta</H2>
        <P>
          DeepSeek es un modelo de lenguaje desarrollado por DeepSeek AI (China) que en 2025 alcanzó
          niveles de rendimiento comparables a GPT-4o en tareas de razonamiento acotado y clasificación,
          a una fracción del coste de inferencia. El modelo R1 está optimizado específicamente para
          cadenas de razonamiento paso a paso.
        </P>

        <DataBox
          stat="~$0.27"
          label="por millón de tokens de entrada con DeepSeek V3 (frente a ~$2.50 de GPT-4o)"
          source="Precios públicos de inferencia, Enero 2026"
          color="cyan"
        />

        <H3>Tareas donde DeepSeek destaca</H3>
        <Ul>
          <Li>Clasificación de documentos, leads y tickets de soporte a alto volumen.</Li>
          <Li>Extracción de campos estructurados de formularios, contratos breves y correos.</Li>
          <Li>Razonamiento acotado: "dado este lead con estos atributos, cuál es el siguiente paso en el pipeline".</Li>
          <Li>Resúmenes de textos medianos donde no se requiere comprensión de matices culturales complejos.</Li>
          <Li>Operaciones repetitivas donde el coste por tarea impacta directamente en la rentabilidad del agente.</Li>
        </Ul>

        <H3>Cuándo NO usar DeepSeek</H3>
        <Ul>
          <Li>Generación creativa larga que requiera un tono muy específico o matices culturales finos.</Li>
          <Li>Análisis de contextos multimodales (imagen + texto) donde GPT-4o tiene ventaja nativa.</Li>
          <Li>Casos donde el cumplimiento regulatorio exija un proveedor con sede en la UE o EE.UU.</Li>
        </Ul>

        {/* ── SECCIÓN 3 ── */}
        <H2>GPT-4o / GPT-4o-mini: cuándo es la elección correcta</H2>
        <P>
          GPT-4o es el modelo de referencia para casos de uso generales. Su mayor ventaja no es siempre
          el rendimiento en tareas específicas, sino la compatibilidad con el ecosistema de herramientas
          de OpenAI, la madurez de su API y la amplia documentación disponible para integraciones complejas.
          GPT-4o-mini ofrece un perfil de coste más competitivo para tareas de complejidad media.
        </P>

        <H3>Tareas donde GPT-4o destaca</H3>
        <Ul>
          <Li>Conversaciones largas con cambio de contexto frecuente y muchos giros.</Li>
          <Li>Tareas multimodales: análisis de imágenes, documentos escaneados, capturas de pantalla.</Li>
          <Li>Generación de contenido con tono muy específico: emails comerciales, propuestas personalizadas.</Li>
          <Li>Integración con herramientas del ecosistema OpenAI (Assistants API, function calling avanzado).</Li>
          <Li>Contextos donde la latencia importa menos que la versatilidad del modelo.</Li>
        </Ul>

        <H3>Cuándo NO es la primera opción</H3>
        <Ul>
          <Li>Operaciones masivas de clasificación o extracción donde el coste escala de forma significativa.</Li>
          <Li>Tareas de razonamiento paso a paso donde Claude o DeepSeek R1 tienen mejor rendimiento verificado.</Li>
        </Ul>

        {/* ── SECCIÓN 4 ── */}
        <H2>Claude Haiku / Sonnet: cuándo es la elección correcta</H2>
        <P>
          Anthropic ha posicionado a Claude como el modelo de referencia para tareas que requieren
          seguimiento preciso de instrucciones complejas y cadenas de razonamiento largas.
          Claude Haiku es el modelo más económico de la familia. Claude Sonnet ofrece capacidad
          superior para análisis profundo. Claude Opus es el más potente pero también el más costoso.
        </P>

        <H3>Tareas donde Claude destaca</H3>
        <Ul>
          <Li>Revisión y análisis de contratos largos donde se necesita precisión en detalles legales.</Li>
          <Li>Redacción técnica con muchas restricciones explícitas ("no uses el término X", "el tono debe ser Y").</Li>
          <Li>Cadenas de razonamiento donde el modelo debe justificar cada paso de forma transparente.</Li>
          <Li>Contextos donde la respuesta de instrucciones es crítica: prompts muy detallados con muchas condiciones.</Li>
          <Li>Análisis de documentos extensos donde el contexto largo importa.</Li>
        </Ul>

        <H3>Cuándo NO es la primera opción</H3>
        <Ul>
          <Li>Clasificación masiva y repetitiva donde el coste de Claude Sonnet es significativamente mayor.</Li>
          <Li>Tasks cortas y de baja complejidad donde Haiku o DeepSeek son suficientes.</Li>
        </Ul>

        {/* ── SECCIÓN 5 ── */}
        <H2>Kimi k1.5: el modelo para contextos muy largos</H2>
        <P>
          Kimi, desarrollado por Moonshot AI (China), es menos conocido en el mercado occidental pero
          ha mostrado rendimiento sólido en tareas que requieren ventanas de contexto extendidas a
          precio competitivo. Su punto diferencial es la capacidad de procesar documentos muy largos
          sin degradación de calidad en las partes finales del texto.
        </P>

        <H3>Tareas donde Kimi destaca</H3>
        <Ul>
          <Li>Revisión de transcripciones largas de reuniones o llamadas de ventas.</Li>
          <Li>Análisis de informes extensos o informes anuales de empresa.</Li>
          <Li>Síntesis de múltiples documentos relacionados en un único resumen coherente.</Li>
          <Li>Revisión de código en bases de código medianas o grandes.</Li>
        </Ul>

        {/* ── TABLA COMPARATIVA ── */}
        <H2>Resumen: cuándo usar qué</H2>
        <P>Esta tabla no es una sentencia. Es una guía de punto de partida para el enrutamiento:</P>

        <ModelRow
          model="DeepSeek V3 / R1"
          task="Clasificación, extracción, razonamiento acotado, resúmenes breves. Alto volumen. Coste mínimo."
          verdict="Elección por defecto en operaciones masivas"
          color="border-cyan-400/20 bg-cyan-400/5"
        />
        <ModelRow
          model="GPT-4o / GPT-4o-mini"
          task="Conversaciones complejas, tareas multimodales, generación de contenido con tono específico."
          verdict="Elección por defecto en versatilidad"
          color="border-emerald-400/20 bg-emerald-400/5"
        />
        <ModelRow
          model="Claude Haiku / Sonnet"
          task="Análisis de contratos, redacción técnica precisa, cadenas de razonamiento largas."
          verdict="Elección por defecto en razonamiento profundo"
          color="border-violet-400/20 bg-violet-400/5"
        />
        <ModelRow
          model="Kimi k1.5"
          task="Documentos muy largos, transcripciones, síntesis de múltiples fuentes."
          verdict="Elección por defecto en contexto extendido"
          color="border-amber-400/20 bg-amber-400/5"
        />

        {/* ── SECCIÓN 6 ── */}
        <H2>Cómo Aynara gestiona el enrutamiento</H2>
        <P>
          En Neuralia 369, el enrutamiento por modelo no es manual salvo que el cliente lo especifique.
          Aynara analiza el tipo de tarea, el volumen estimado y los requisitos de precisión, y asigna
          el modelo más eficiente para esa operación. El cliente puede fijar un modelo por agente desde
          el panel si lo prefiere.
        </P>
        <P>
          El coste de inferencia por modelo aparece desglosado en la factura mensual. Si el enrutamiento
          automático usa DeepSeek para el 80 % de las clasificaciones y GPT-4o para el 20 % restante,
          la factura refleja exactamente ese mix, con el coste de cada modelo por separado.
        </P>
        <P>
          Para ver cuánto costaría tu volumen estimado con cada modelo, puedes usar la{' '}
          <Link to="/integraciones/deepseek#calculadora" className="text-violet-300 hover:text-white underline underline-offset-4 decoration-violet-400/40 transition-colors">
            calculadora de consumo en la página de integraciones
          </Link>.
        </P>
      </ArticleLayout>
    </>
  );
}
